/**
 * Build-time responsive-variant lint for DCS customer sites.
 *
 * The 2026-07-03 flagship-excellence review (P7) found desktop pages shipping
 * un-suffixed **full-size** CDN images (e.g. a 321.9 KB hero `47af1e8f….webp`)
 * instead of a `-md`/`-lg` responsive step. That happens when an image reaches
 * the output HTML through a path that does NOT run the `<img>` → `<picture>`
 * transform — content injected via `__DCS_CONTENT__` and then swapped to the
 * base `cdnUrl` by `dcsCdnBuildEnd`, or a hand-authored `<img src>` pointing at
 * a base CDN asset URL.
 *
 * A DCS CDN asset URL has the shape
 *   `https://files.<host>/[content/]<slug>/assets/[<dir>/]<uuid>.<ext>`
 * Responsive variants insert a `-sm`/`-md`/`-lg` suffix before the extension
 * (`…<uuid>-md.webp`). Those suffixes contain non-hex letters, so a URL that
 * still matches the *base* pattern below is, by definition, an un-suffixed
 * full-size asset. This lint scans the built HTML's `<img src>` and
 * `<source srcset>` attributes for such URLs and reports (or fails on) them.
 *
 * It is intentionally a **static-output** lint (VitePress generates HTML after
 * both Vite builds finish, so a Vite plugin hook cannot see the SSG output) and
 * is registered via VitePress `buildEnd`.
 *
 * @example
 * ```ts
 * // .vitepress/config.ts
 * import { dcsCdnBuildEnd, dcsResponsiveImageLint, chainBuildEnd } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   // run the CDN rewrite first, then lint the result
 *   buildEnd: chainBuildEnd(dcsCdnBuildEnd(), dcsResponsiveImageLint()),
 * })
 * ```
 */

import fs from 'node:fs'
import path from 'node:path'

// ────────────────────────────────────────────────────────────────────────────
// Detector (pure — unit-testable without a build)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Anchored matcher for a **base** (un-suffixed, full-size) DCS CDN asset URL.
 * Kept aligned with `responsiveImagePlugin`'s `CDN_PATTERN`: the filename stem
 * is `[a-f0-9-]+` (hex UUID), so a responsive variant — `…-sm.webp`,
 * `…-md.webp`, `…-lg.webp` — never matches (each suffix carries a non-hex
 * letter). Query strings / fragments are tolerated after the extension.
 */
const BASE_CDN_ASSET =
  /^https?:\/\/files\.[^/]+\/(?:content\/)?[^/]+\/assets\/(?:[^/]+\/)*[a-f0-9-]+\.\w+(?:[?#].*)?$/i

export interface ResponsiveImageViolation {
  /** The un-suffixed full-size CDN URL that should have been a responsive variant. */
  url: string
  /** Which element referenced it. */
  tag: 'img' | 'source'
}

/** True when `url` is a base (un-suffixed, full-size) DCS CDN asset URL. */
export function isFullSizeCdnUrl(url: string): boolean {
  return BASE_CDN_ASSET.test(url.trim())
}

function readAttr(tag: string, name: string): string | null {
  const m = tag.match(new RegExp(`\\b${name}=("([^"]*)"|'([^']*)')`, 'i'))
  if (!m) return null
  return m[2] ?? m[3] ?? ''
}

/**
 * Scan an HTML string and return every `<img src>` / `<source srcset>` reference
 * that resolves to a base (un-suffixed) full-size DCS CDN asset. `srcset`
 * candidates are split on commas and only the URL portion (before the width /
 * density descriptor) is tested. `onerror` and other attributes are ignored —
 * only the actual `src` / `srcset` sources are inspected.
 */
export function findFullSizeCdnImages(html: string): ResponsiveImageViolation[] {
  const violations: ResponsiveImageViolation[] = []

  for (const m of html.matchAll(/<img\b[^>]*>/gi)) {
    const src = readAttr(m[0], 'src')
    if (src && isFullSizeCdnUrl(src)) {
      violations.push({ url: src.trim(), tag: 'img' })
    }
  }

  for (const m of html.matchAll(/<source\b[^>]*>/gi)) {
    const srcset = readAttr(m[0], 'srcset')
    if (!srcset) continue
    for (const candidate of srcset.split(',')) {
      const url = candidate.trim().split(/\s+/)[0]
      if (url && isFullSizeCdnUrl(url)) {
        violations.push({ url, tag: 'source' })
      }
    }
  }

  return violations
}

// ────────────────────────────────────────────────────────────────────────────
// VitePress buildEnd hook
// ────────────────────────────────────────────────────────────────────────────

export interface DcsResponsiveImageLintOptions {
  /**
   * Throw (fail the build) when full-size images are found. Default `false`
   * (warn only) so the guard can land ahead of the per-site cleanup; flip to
   * `true` in CI once a site's output is clean to prevent regressions.
   */
  failOnViolation?: boolean
  /** File extensions to scan in the output directory. Default `['.html']`. */
  extensions?: string[]
  /** Enable per-file debug logging. */
  debug?: boolean
}

export interface ResponsiveImageLintReport {
  filesScanned: number
  violations: Array<ResponsiveImageViolation & { file: string }>
}

/**
 * VitePress `buildEnd` factory that lints the generated HTML for un-suffixed
 * full-size CDN images. Returns the report (also useful for tests) and either
 * warns or throws depending on `failOnViolation`.
 */
export function dcsResponsiveImageLint(options: DcsResponsiveImageLintOptions = {}) {
  const { failOnViolation = false, extensions = ['.html'], debug = false } = options

  return async (siteConfig: { outDir: string }): Promise<ResponsiveImageLintReport> => {
    const report: ResponsiveImageLintReport = { filesScanned: 0, violations: [] }
    const outDir = siteConfig.outDir
    if (!outDir || !fs.existsSync(outDir)) return report

    const walk = (dir: string) => {
      for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
        const full = path.join(dir, entry.name)
        if (entry.isDirectory()) {
          walk(full)
        } else if (extensions.some((ext) => entry.name.endsWith(ext))) {
          report.filesScanned++
          const html = fs.readFileSync(full, 'utf8')
          if (!html.includes('/assets/')) continue
          const rel = path.relative(outDir, full)
          for (const v of findFullSizeCdnImages(html)) {
            report.violations.push({ ...v, file: rel })
            if (debug) {
              console.log(`[dcs-responsive-lint] ${rel}: <${v.tag}> full-size ${v.url}`)
            }
          }
        }
      }
    }

    walk(outDir)

    if (report.violations.length > 0) {
      const lines = report.violations
        .map((v) => `  ${v.file}: <${v.tag}> ${v.url}`)
        .join('\n')
      const summary =
        `[dcs-responsive-lint] ${report.violations.length} full-size CDN image(s) ` +
        `shipped without a -sm/-md/-lg responsive variant across ${report.filesScanned} file(s):\n${lines}`
      if (failOnViolation) {
        throw new Error(summary)
      }
      console.warn(summary)
    } else if (debug) {
      console.log(
        `[dcs-responsive-lint] clean — scanned ${report.filesScanned} file(s), no full-size images.`
      )
    }

    return report
  }
}

// ────────────────────────────────────────────────────────────────────────────
// buildEnd composition helper
// ────────────────────────────────────────────────────────────────────────────

/**
 * Compose multiple VitePress `buildEnd` hooks into one (VitePress accepts a
 * single `buildEnd`). Hooks run sequentially in the order given, so a rewrite
 * hook (`dcsCdnBuildEnd`) can precede a lint (`dcsResponsiveImageLint`).
 */
export function chainBuildEnd<T>(
  ...hooks: Array<((siteConfig: T) => unknown | Promise<unknown>) | undefined | null>
): (siteConfig: T) => Promise<void> {
  const active = hooks.filter(
    (h): h is (siteConfig: T) => unknown | Promise<unknown> => typeof h === 'function'
  )
  return async (siteConfig: T) => {
    for (const hook of active) {
      await hook(siteConfig)
    }
  }
}
